import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IBasicModalProps } from '../../interfaces/IBasicModalProps';
import { CalendarEventData } from '../types/CalendarEventData';
import { useFormikContext } from 'formik';
import { MbscCalendarEventData } from '@mobiscroll/react';
import { RecurrenceUpdateMode } from '../Enums/RecurrenceUpdateModeEnum';
import { RRule } from 'rrule';
import { cleanRRuleString } from '../Utils/recurrenceUtils';

export interface IRecurrenceUpdateStrategyModalProps extends IBasicModalProps {
    createEvent: (values: CalendarEventData) => Promise<void>;
    updateEvent: (values: CalendarEventData) => Promise<void>;
    eventList: MbscCalendarEventData[];
}

const formatIsoDateOnly = (date: Date): string => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const RecurrenceUpdateStrategyModal = (props: IRecurrenceUpdateStrategyModalProps) => {
    const formikContext = useFormikContext<CalendarEventData>();
    const { values } = formikContext;
    const { open, onClose, onSuccess, updateEvent, createEvent, eventList } = props;

    const originalEvent = eventList?.find(
        (x: any) => x.id === values.id || x.Id === values.Id || x.Id === values.id || x.id === values.Id
    ) as CalendarEventData;

    const isRecurrenceChanged = Boolean(
        originalEvent &&
        cleanRRuleString(values.recurring as string) !== cleanRRuleString(originalEvent.recurring as string)
    );

    const [selectedMode, setSelectedMode] = useState<RecurrenceUpdateMode>(
        isRecurrenceChanged ? RecurrenceUpdateMode.Future : RecurrenceUpdateMode.Single
    );

    useEffect(() => {
        if (open) {
            const changed = Boolean(
                originalEvent &&
                cleanRRuleString(values.recurring as string) !== cleanRRuleString(originalEvent.recurring as string)
            );
            setSelectedMode(changed ? RecurrenceUpdateMode.Future : RecurrenceUpdateMode.Single);
        }
    }, [open, values.recurring, originalEvent]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedMode((event.target as HTMLInputElement).value as RecurrenceUpdateMode);
    };

    const handleClose = () => {
        setSelectedMode(RecurrenceUpdateMode.Single);
        onClose();
    };

    const handleSuccess = async () => {
        const memberObjectId = values.MemberObjectId || originalEvent?.MemberObjectId;
        const memberName = values.MemberName || originalEvent?.MemberName;
        const memberAHCCCSId = values.MemberAHCCCSId || originalEvent?.MemberAHCCCSId;

        const originalOccurrenceStart = new Date(values.start ?? values.startDate);
        const originalOccurrenceEnd = new Date(values.end ?? values.endDate);
        const updatedStart = new Date(values.startDate ?? values.start);
        const updatedEnd = new Date(values.endDate ?? values.end);

        switch (selectedMode) {
            case RecurrenceUpdateMode.Single: {
                if (originalEvent) {
                    const formattedExceptionDate = formatIsoDateOnly(originalOccurrenceStart);
                    const existingExceptions = (originalEvent.recurringException as string[]) ?? [];
                    if (!existingExceptions.includes(formattedExceptionDate)) {
                        const updatedExceptions = [...existingExceptions, formattedExceptionDate];
                        originalEvent.recurringException = updatedExceptions;
                        originalEvent.ExceptionDates = updatedExceptions;
                    }
                }

                // The new single event gets its recurring data scrubbed out of caution.
                const newEvent: CalendarEventData = {
                    ...values,
                    MemberObjectId: memberObjectId,
                    MemberName: memberName,
                    MemberAHCCCSId: memberAHCCCSId,
                    start: updatedStart as any,
                    end: updatedEnd as any,
                    startDate: updatedStart,
                    endDate: updatedEnd,
                    AppointmentDateTime: updatedStart,
                    AppointmentEndDateTime: updatedEnd,
                    recurring: undefined,
                    RecurrenceString: undefined,
                    recurringException: [],
                    ExceptionDates: [],
                };
                delete newEvent.id;
                delete newEvent.Id;

                try {
                    await createEvent(newEvent);
                    if (originalEvent) {
                        await updateEvent(originalEvent);
                    }
                } catch (error) {
                    console.error('Error updating single recurring instance:', error);
                }
                
                break;
            }
            case RecurrenceUpdateMode.Future: {
                try {
                    const origStartDate = originalEvent ? new Date(originalEvent.startDate ?? originalEvent.start) : originalOccurrenceStart;

                    if (origStartDate.toDateString() === originalOccurrenceStart.toDateString()) {
                        const cleanRule = cleanRRuleString(values.recurring as string);
                        const updatedSeriesValues: CalendarEventData = {
                            ...values,
                            MemberObjectId: memberObjectId,
                            MemberName: memberName,
                            MemberAHCCCSId: memberAHCCCSId,
                            start: updatedStart as any,
                            end: updatedEnd as any,
                            startDate: updatedStart,
                            endDate: updatedEnd,
                            AppointmentDateTime: updatedStart,
                            AppointmentEndDateTime: updatedEnd,
                            recurring: cleanRule,
                            RecurrenceString: cleanRule,
                        };
                        await updateEvent(updatedSeriesValues);
                    } else if (originalEvent) {
                        const dayBeforeUtc = new Date(Date.UTC(
                            originalOccurrenceStart.getFullYear(),
                            originalOccurrenceStart.getMonth(),
                            originalOccurrenceStart.getDate() - 1,
                            23, 59, 59
                        ));

                        const oldRecRule = RRule.fromString(cleanRRuleString(originalEvent.recurring as string));
                        const oldOptions = { ...oldRecRule.origOptions };
                        delete oldOptions.dtstart;
                        const updatedOldRule = new RRule({
                            ...oldOptions,
                            until: dayBeforeUtc,
                            count: undefined,
                        });
                        const oldRuleStr = cleanRRuleString(updatedOldRule.toString());
                        originalEvent.recurring = oldRuleStr;
                        originalEvent.RecurrenceString = oldRuleStr;

                        const rawRecRule = cleanRRuleString((values.recurring || originalEvent.recurring) as string);
                        const newRecRuleOptions = RRule.fromString(rawRecRule).origOptions;
                        const newOptions = { ...newRecRuleOptions };
                        delete newOptions.dtstart;
                        delete newOptions.until;
                        delete newOptions.count;
                        const newSeriesRule = new RRule(newOptions);
                        const newSeriesRuleStr = cleanRRuleString(newSeriesRule.toString());

                        const newSeriesEvent: CalendarEventData = {
                            ...values,
                            MemberObjectId: memberObjectId,
                            MemberName: memberName,
                            MemberAHCCCSId: memberAHCCCSId,
                            start: updatedStart as any,
                            end: updatedEnd as any,
                            startDate: updatedStart,
                            endDate: updatedEnd,
                            AppointmentDateTime: updatedStart,
                            AppointmentEndDateTime: updatedEnd,
                            recurring: newSeriesRuleStr,
                            RecurrenceString: newSeriesRuleStr,
                            recurringException: [],
                            ExceptionDates: [],
                        };
                        delete newSeriesEvent.id;
                        delete newSeriesEvent.Id;

                        await createEvent(newSeriesEvent);
                        await updateEvent(originalEvent);
                    }
                } catch (error) {
                    console.error('Error updating future recurring instances:', error);
                }
                break;
            }
            default:
                console.error('Invalid recurrence update mode');
                break;
        }
        setSelectedMode(RecurrenceUpdateMode.Single);
        onSuccess();
    };

    return (
        <Dialog open={open} hideBackdrop>
            <DialogTitle>Update Recurring Appointment</DialogTitle>
            <DialogContent>
                <RadioGroup aria-label="recurrence-update-mode" value={selectedMode} onChange={handleChange}>
                    {Object.keys(RecurrenceUpdateMode).map((mode) => (
                        <FormControlLabel key={mode} value={RecurrenceUpdateMode[mode]} control={<Radio />} label={RecurrenceUpdateMode[mode]} />
                    ))}
                </RadioGroup>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSuccess}>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RecurrenceUpdateStrategyModal;
